/**
 * Relation Cell View
 *
 * Cells display relations only - NO config here!
 * Config is accessed ONLY via Column Header Menu.
 */
import { Popover, uniReactRoot } from '@affine/component';
import { useI18n } from '@affine/i18n';
import type { DatabaseBlockDataSource } from '@blocksuite/affine/blocks/database';
import {
  type Cell,
  type CellRenderProps,
  createIcon,
  type DataViewCellLifeCycle,
} from '@blocksuite/affine/blocks/database';
import { LinkedPageIcon } from '@blocksuite/icons/rc';
import { computed, type ReadonlySignal } from '@preact/signals-core';
import {
  forwardRef,
  type ForwardRefRenderFunction,
  type ReactNode,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';

import { useSignalValue } from '../../../../modules/doc-info/utils';
import { canWriteRelationEndpoint } from './access-state';
import { findDatabaseByBlockId } from './database-discovery-bridge';
import type {
  RelationCellJsonValue,
  RelationCellRawValue,
  RelationPropertyData,
} from './define';
import { relationPropertyModelConfig } from './define';
import {
  resolveRelationDisplayValue,
  useRelationDisplayValue,
  useRelationTargetStoreProvider,
} from './display-resolver';
import { RelationDropdown } from './dropdown';
import { useStealDialog } from './steal-dialog';
import * as styles from './style.css';
import { processRelationChange, type StealInfo } from './sync';

const MAX_VISIBLE_RELATION_ENTRIES = 10;

/**
 * Manager for Relation-Cell logic
 */
class RelationManager {
  readonly cell: Cell<
    RelationCellRawValue,
    RelationCellJsonValue,
    RelationPropertyData
  >;
  readonly selectCurrentCell: (editing: boolean) => void;
  readonly isEditing: ReadonlySignal<boolean>;

  relationList = computed(() => this.cell.value$.value ?? []);

  get propertyData() {
    return this.cell.property.data$.value;
  }

  get readonly() {
    return this.cell.property.readonly$;
  }

  private get dataSource(): DatabaseBlockDataSource {
    return this.cell.view.manager.dataSource as DatabaseBlockDataSource;
  }

  get store() {
    return this.dataSource.doc;
  }

  // Get the database ID that contains this cell
  get sourceDatabaseId(): string {
    return this.dataSource.blockId ?? '';
  }

  // Get the property ID (relation column in source DB)
  get sourcePropertyId(): string {
    return this.cell.property.id;
  }

  constructor(
    props: CellRenderProps<
      RelationPropertyData,
      RelationCellRawValue,
      RelationCellJsonValue
    >
  ) {
    this.cell = props.cell;
    this.selectCurrentCell = props.selectCurrentCell;
    this.isEditing = props.isEditing$;
  }

  setRelationList = (relationIds: string[]): void => {
    this.cell.valueSet(relationIds);
  };
}

/**
 * Relation Cell Component
 */
const RelationCellComponent: ForwardRefRenderFunction<
  DataViewCellLifeCycle,
  CellRenderProps<
    RelationPropertyData,
    RelationCellRawValue,
    RelationCellJsonValue
  >
> = (props, ref): ReactNode => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const manager = useMemo(() => new RelationManager(props), []);
  const { showStealDialog, DialogComponent } = useStealDialog();
  const t = useI18n();

  useImperativeHandle(
    ref,
    () => ({
      beforeEnterEditMode: () => true,
      beforeExitEditingMode: () => {},
      afterEnterEditingMode: () => {},
      focusCell: () => true,
      blurCell: () => true,
      forceUpdate: () => {},
    }),
    []
  );

  const relationList = useSignalValue(manager.relationList);
  const isEditing = useSignalValue(manager.isEditing);
  const propertyData = manager.propertyData;
  const isUnconfigured = !propertyData.targetDatabaseId;
  const targetStoreProvider = useRelationTargetStoreProvider();
  const targetDatabaseDocId = propertyData.targetDatabaseId
    ? (findDatabaseByBlockId(propertyData.targetDatabaseId)?.docId ??
      propertyData.targetDocId)
    : undefined;

  // Handler for value changes
  const handleValueChange = useCallback(
    async (newValue: string[]) => {
      const { targetDatabaseId, inverseFieldId } = propertyData;

      if (!inverseFieldId) {
        console.warn(
          '[RelationCell] Blocked forward-only relation write: inverseFieldId is missing',
          { targetDatabaseId, sourcePropertyId: manager.sourcePropertyId }
        );
        return;
      }
      if (!targetDatabaseDocId) {
        throw new Error(
          `Relation target database is not registered: ${targetDatabaseId}`
        );
      }
      if (
        !canWriteRelationEndpoint({
          targetDatabaseId,
          targetDocId: targetDatabaseDocId,
        })
      ) {
        console.warn(
          '[RelationCell] Blocked relation write to read-only target',
          {
            targetDatabaseId,
            targetDocId: targetDatabaseDocId,
          }
        );
        return;
      }

      const handleStealDialog = async (info: StealInfo): Promise<boolean> => {
        const newOwnerDisplay = resolveRelationDisplayValue({
          targetStoreProvider,
          rowId: info.targetRowId,
          targetDatabaseId: propertyData.targetDatabaseId,
          targetFieldId: propertyData.targetFieldId,
        }).value;
        return showStealDialog(info, newOwnerDisplay);
      };

      // Use stored inverseCardinality, default to 'many' if not set (many-to-many safe default)
      const actualInverseCardinality =
        propertyData.inverseCardinality || 'many';

      try {
        const finalValue = await processRelationChange(
          manager.store,
          manager.sourceDatabaseId,
          manager.sourcePropertyId,
          manager.cell.rowId,
          targetDatabaseId,
          inverseFieldId,
          actualInverseCardinality,
          relationList,
          newValue,
          handleStealDialog,
          targetDatabaseDocId
        );

        manager.setRelationList(finalValue);
      } catch (error) {
        console.warn('[RelationCell] Relation update failed:', error);
      }
    },
    [
      propertyData,
      manager,
      showStealDialog,
      relationList,
      targetStoreProvider,
      targetDatabaseDocId,
    ]
  );

  const maxVisible =
    propertyData.cardinality === 'one'
      ? 1
      : Math.min(relationList.length, MAX_VISIBLE_RELATION_ENTRIES);
  const visibleItems = relationList.slice(0, maxVisible);
  const remainingCount = relationList.length - maxVisible;

  // Unconfigured State - show hint to configure via column header
  if (isUnconfigured) {
    return (
      <div className={styles.cellContainer}>
        <div className={styles.unconfiguredState}>
          {t['com.streamlain.streamlain.relation.configure-column-header']()}
        </div>
      </div>
    );
  }

  // Dropdown content for selecting relations
  const renderPopoverContent = () => (
    <RelationDropdown
      store={manager.store}
      targetDatabaseId={propertyData.targetDatabaseId}
      targetDocId={targetDatabaseDocId}
      targetFieldId={propertyData.targetFieldId}
      cardinality={propertyData.cardinality}
      currentValue={relationList}
      onChange={(newValue: string[]) => void handleValueChange(newValue)}
      onClose={() => manager.selectCurrentCell(false)}
    />
  );

  // Empty State (configured but no relations yet)
  if (relationList.length === 0) {
    return (
      <>
        {DialogComponent}
        <Popover
          open={isEditing}
          onOpenChange={open => manager.selectCurrentCell(open)}
          contentOptions={{ className: styles.popoverContent }}
          content={renderPopoverContent()}
        >
          <div className={styles.cellContainer}>
            <div
              className={styles.emptyState}
              onClick={() => manager.selectCurrentCell(true)}
            >
              {/* Empty - click to add */}
            </div>
          </div>
        </Popover>
      </>
    );
  }

  // Relation Chips - using visibleItems/remainingCount calculated above
  return (
    <>
      {DialogComponent}
      <Popover
        open={isEditing}
        onOpenChange={open => manager.selectCurrentCell(open)}
        contentOptions={{ className: styles.popoverContent }}
        content={renderPopoverContent()}
      >
        <div
          className={styles.cellContainer}
          onClick={() => manager.selectCurrentCell(true)}
        >
          {visibleItems.map(rowId => (
            <RelationChip
              key={rowId}
              rowId={rowId}
              targetStoreProvider={targetStoreProvider}
              targetDatabaseId={propertyData.targetDatabaseId}
              targetDocId={targetDatabaseDocId}
              targetFieldId={propertyData.targetFieldId}
            />
          ))}
          {remainingCount > 0 && (
            <span className={styles.moreCount}>+{remainingCount}</span>
          )}
        </div>
      </Popover>
    </>
  );
};

/**
 * Single Relation Chip
 */
const RelationChip = ({
  rowId,
  targetStoreProvider,
  targetDatabaseId,
  targetDocId,
  targetFieldId,
}: {
  rowId: string;
  targetStoreProvider: ReturnType<typeof useRelationTargetStoreProvider>;
  targetDatabaseId: string;
  targetDocId?: string;
  targetFieldId: string;
}) => {
  const displayValue = useRelationDisplayValue({
    targetStoreProvider,
    rowId,
    targetDatabaseId,
    targetDocId,
    targetFieldId,
  });
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // TODO: Navigation to Target-Row (T002 Enhancement)
    console.log('Navigate to row:', rowId);
  };

  return (
    <div className={styles.relationChip} onClick={handleClick}>
      <span className={styles.relationIcon}>
        <LinkedPageIcon style={{ width: '12px', height: '12px' }} />
      </span>
      <span className={styles.relationText}>{displayValue}</span>
    </div>
  );
};

const RelationCell = forwardRef(RelationCellComponent);

/**
 * Property Config - registers the Relation type
 */
export const relationPropertyConfig =
  relationPropertyModelConfig.createPropertyMeta({
    icon: createIcon('DualLinkIcon'),
    cellRenderer: {
      view: uniReactRoot.createUniComponent(RelationCell),
    },
  });
